import * as React from 'react';
import { cva } from 'class-variance-authority';
import { PiCaretDown, PiCaretUp, PiPlus, PiX } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';

/* ------------------------------------------------------------------ */
/* Zone 2: State (the only elastic zone)                              */
/* ------------------------------------------------------------------ */

export const TableDockState = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    className={cn(
      'flex min-w-0 flex-1 items-center gap-2 overflow-hidden',
      className,
    )}
    {...props}
  />
);

export const chipVariants = cva(
  'inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full text-[11.5px] font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
  {
    variants: {
      variant: {
        filter: 'bg-primary/[0.08] pl-2 pr-1 text-primary',
        sort: 'gap-1 bg-muted px-2 text-secondary-foreground',
        selection: 'bg-primary pl-2 pr-1 text-white',
        add: 'gap-1 border border-dashed border-input px-2 text-muted-foreground transition-colors hover:border-primary hover:text-primary',
      },
    },
    defaultVariants: { variant: 'filter' },
  },
);

export interface FilterChipProps extends React.ComponentProps<'div'> {
  /** Reads "Field: value", e.g. "Vessel type: Cargo, Tanker" or "SOG > 5 kn". */
  label: React.ReactNode;
  /** Removes this filter and re-runs the query. */
  onRemove: () => void;
}

export const TableDockFilterChip = ({
  label,
  onRemove,
  className,
  ...props
}: FilterChipProps) => (
  <div
    className={cn(chipVariants({ variant: 'filter' }), className)}
    {...props}
  >
    <span className="truncate">{label}</span>
    <button
      type="button"
      onClick={onRemove}
      aria-label="Remove filter"
      className="ml-1 flex size-[15px] shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/[0.16] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      <PiX className="size-[9px]" />
    </button>
  </div>
);

export interface SortChipProps extends React.ComponentProps<'button'> {
  /** The sorted column's label, e.g. "Name". */
  label: React.ReactNode;
  /** Sort direction; controls the trailing caret. */
  dir: 'asc' | 'desc';
}

export const TableDockSortChip = ({
  label,
  dir,
  className,
  ...props
}: SortChipProps) => (
  <button
    type="button"
    className={cn(chipVariants({ variant: 'sort' }), className)}
    {...props}
  >
    <span className="truncate">Sorted: {label}</span>
    {dir === 'asc' ? (
      <PiCaretUp className="size-[9px] text-primary" />
    ) : (
      <PiCaretDown className="size-[9px] text-primary" />
    )}
  </button>
);

export interface SelectionChipProps extends React.ComponentProps<'div'> {
  /** Number of selected rows. */
  count: number;
  /** Clears the selection. */
  onClear: () => void;
}

export const TableDockSelectionChip = ({
  count,
  onClear,
  className,
  ...props
}: SelectionChipProps) => (
  <div
    className={cn(chipVariants({ variant: 'selection' }), className)}
    {...props}
  >
    <span className="truncate">{count} selected</span>
    <button
      type="button"
      onClick={onClear}
      aria-label="Clear selection"
      className="ml-1 flex size-[15px] shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      <PiX className="size-[9px]" />
    </button>
  </div>
);

export interface AddFilterProps extends React.ComponentProps<'button'> {
  label?: React.ReactNode;
}

export const TableDockAddFilter = React.forwardRef<
  HTMLButtonElement,
  AddFilterProps
>(({ label = 'Filter', className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(chipVariants({ variant: 'add' }), className)}
    {...props}
  >
    <PiPlus className="size-[11px]" />
    {label}
  </button>
));
TableDockAddFilter.displayName = 'TableDock.AddFilter';
